/* Settings-bar controls for the 3D surface view. */

import { ViewIds } from '@ardoq/api-types';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { SettingsType, type SettingsConfig } from '@ardoq/view-settings';
import type {
  BinCount,
  CoincidentAggregation,
  PerValueAxes,
  Surface3DViewSettings,
  ZInterpolation,
} from '@ardoq/xyz-charts';
import { noop } from 'lodash';
import { handleStoreSetting } from 'viewSettings/settingsHelper';

const VIEW_ID = ViewIds.SURFACE_3D;

type NumericField = { name: string; label: string };

/** Which dimension a field picker sets, and how it is labelled. */
type FieldDimension = {
  key: 'selectedFieldNameX' | 'selectedFieldNameY' | 'selectedFieldNameZ';
  id: string;
  iconName: IconName;
  i18nKey: string;
  defaultLabel: string;
};

const FIELD_DIMENSIONS: FieldDimension[] = [
  {
    key: 'selectedFieldNameX',
    id: 'surface3dFieldX',
    iconName: IconName.ARROW_FORWARD,
    i18nKey: 'views:surface3d.menu.xField',
    defaultLabel: 'X-axis field',
  },
  {
    key: 'selectedFieldNameY',
    id: 'surface3dFieldY',
    iconName: IconName.ARROW_UPWARD,
    i18nKey: 'views:surface3d.menu.yField',
    defaultLabel: 'Y-axis field',
  },
  {
    key: 'selectedFieldNameZ',
    id: 'surface3dFieldZ',
    iconName: IconName.BAR_CHART,
    i18nKey: 'views:surface3d.menu.zField',
    defaultLabel: 'Height field',
  },
];

const INTERPOLATION_OPTIONS: [ZInterpolation, string, string][] = [
  [
    'linear',
    'Between components (linear)',
    'views:surface3d.interpolation.linear',
  ],
  [
    'smooth',
    'Smoothed across the plane',
    'views:surface3d.interpolation.smooth',
  ],
  [
    'binned',
    'Only where components are',
    'views:surface3d.interpolation.binned',
  ],
];

const AGGREGATION_OPTIONS: [CoincidentAggregation, string, string][] = [
  ['mean', 'Average', 'views:surface3d.aggregation.mean'],
  ['sum', 'Sum', 'views:surface3d.aggregation.sum'],
  ['max', 'Highest', 'views:surface3d.aggregation.max'],
  ['min', 'Lowest', 'views:surface3d.aggregation.min'],
];

/** Coarse enough to be distinguishable choices rather than a continuous slider nobody tunes. */
const RESOLUTION_OPTIONS: [number, string, string][] = [
  [24, 'Coarse', 'views:surface3d.resolution.coarse'],
  [64, 'Medium', 'views:surface3d.resolution.medium'],
  [128, 'Fine', 'views:surface3d.resolution.fine'],
];

/** Bins per axis, named for the column size each gives. */
const BIN_COUNT_OPTIONS: [number, string, string][] = [
  [8, 'Large', 'views:surface3d.columnSize.large'],
  [16, 'Medium', 'views:surface3d.columnSize.medium'],
  [24, 'Small', 'views:surface3d.columnSize.small'],
];

const fieldPicker = (
  { key, id, iconName, i18nKey, defaultLabel }: FieldDimension,
  viewSettings: Surface3DViewSettings,
  numericFields: NumericField[],
  i18nLocale: I18nLocale
): SettingsConfig => {
  const selected = viewSettings[key];
  const label = t(i18nLocale, i18nKey, defaultLabel);

  return {
    id,
    type: SettingsType.DROPDOWN,
    iconName,
    label,
    buttonText:
      numericFields.find(({ name }) => name === selected)?.label ?? label,
    options: numericFields.map(({ name, label: fieldLabel }) => ({
      name: `${id}_${name}`,
      label: fieldLabel,
      type: DropdownOptionType.OPTION,
      isActive: selected === name,
      onClick: () => handleStoreSetting(VIEW_ID, { [key]: name }),
    })),
    dropdownSize: DropdownSize.S,
    /* Nothing to pick from is a different state than nothing picked, and the empty state
       already explains it; a dropdown of zero options would just be a dead control. */
    isDisabled: numericFields.length === 0,
  };
};

const interpolationDropdown = (
  viewSettings: Surface3DViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'surface3dInterpolation',
  type: SettingsType.DROPDOWN,
  iconName: IconName.LINE_CHART,
  label: t(
    i18nLocale,
    'views:surface3d.menu.interpolation',
    'Height between components'
  ),
  options: INTERPOLATION_OPTIONS.map(([interpolation, label, i18nKey]) => ({
    name: `surface3d_interpolation_${interpolation}`,
    label: t(i18nLocale, i18nKey, label),
    type: DropdownOptionType.OPTION,
    isActive: viewSettings.interpolation === interpolation,
    onClick: () => handleStoreSetting(VIEW_ID, { interpolation }),
  })),
  dropdownSize: DropdownSize.S,
});

const aggregationDropdown = (
  viewSettings: Surface3DViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'surface3dAggregation',
  type: SettingsType.DROPDOWN,
  iconName: IconName.CALCULATOR,
  label: t(
    i18nLocale,
    'views:surface3d.menu.aggregation',
    'Components on the same position'
  ),
  options: AGGREGATION_OPTIONS.map(([aggregation, label, i18nKey]) => ({
    name: `surface3d_aggregation_${aggregation}`,
    label: t(i18nLocale, i18nKey, label),
    type: DropdownOptionType.OPTION,
    isActive: viewSettings.aggregation === aggregation,
    onClick: () => handleStoreSetting(VIEW_ID, { aggregation }),
  })),
  dropdownSize: DropdownSize.S,
});

const resolutionDropdown = (
  viewSettings: Surface3DViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'surface3dResolution',
  type: SettingsType.DROPDOWN,
  iconName: IconName.GRID_ON,
  label: t(i18nLocale, 'views:surface3d.menu.resolution', 'Detail'),
  options: RESOLUTION_OPTIONS.map(([resolution, label, i18nKey]) => ({
    name: `surface3d_resolution_${resolution}`,
    label: t(i18nLocale, i18nKey, label),
    type: DropdownOptionType.OPTION,
    isActive: viewSettings.resolution === resolution,
    onClick: () => handleStoreSetting(VIEW_ID, { resolution }),
  })),
  dropdownSize: DropdownSize.S,
});

type ColumnSizeOption = {
  name: string;
  label: string;
  type: DropdownOptionType.OPTION;
  isActive: boolean;
  onClick: VoidFunction;
};

const PER_VALUE_FIELD_DIMENSIONS: Record<'x' | 'y', FieldDimension> = {
  x: FIELD_DIMENSIONS[0],
  y: FIELD_DIMENSIONS[1],
};

const toPerValueFieldLabel = (
  axis: 'x' | 'y',
  viewSettings: Surface3DViewSettings,
  numericFields: NumericField[],
  i18nLocale: I18nLocale
): string => {
  const { key, i18nKey, defaultLabel } = PER_VALUE_FIELD_DIMENSIONS[axis];
  const selected = viewSettings[key];
  return (
    numericFields.find(({ name }) => name === selected)?.label ??
    t(i18nLocale, i18nKey, defaultLabel)
  );
};

const toSizeOptions = (i18nLocale: I18nLocale): [BinCount, string][] => [
  ['auto', t(i18nLocale, 'views:surface3d.columnSize.automatic', 'Automatic')],
  ...BIN_COUNT_OPTIONS.map(([binCount, label, i18nKey]): [BinCount, string] => [
    binCount,
    t(i18nLocale, i18nKey, label),
  ]),
];

const toColumnSizeOption = (
  binCount: BinCount,
  label: string,
  viewSettings: Surface3DViewSettings
): ColumnSizeOption => ({
  name: `surface3d_columnSize_${binCount}`,
  label,
  type: DropdownOptionType.OPTION,
  isActive: viewSettings.binCount === binCount,
  onClick: () => handleStoreSetting(VIEW_ID, { binCount }),
});

type ColumnSizeOptionsArgs = {
  viewSettings: Surface3DViewSettings;
  perValueAxes: PerValueAxes;
  numericFields: NumericField[];
  i18nLocale: I18nLocale;
};

/**
 * The column sizes on offer. An axis with one bin per value keeps it whatever is picked, so with
 * both axes per value there is nothing to pick, and with one the sizes say they apply to the other.
 */
const toColumnSizeOptions = ({
  viewSettings,
  perValueAxes,
  numericFields,
  i18nLocale,
}: ColumnSizeOptionsArgs): ColumnSizeOption[] => {
  if (perValueAxes === 'both') {
    return [
      {
        name: 'surface3d_columnSize_perValue',
        label: t(
          i18nLocale,
          'views:surface3d.columnSize.perValue',
          'One per value'
        ),
        type: DropdownOptionType.OPTION,
        isActive: true,
        onClick: noop,
      },
    ];
  }

  const sizes = toSizeOptions(i18nLocale);
  if (perValueAxes === 'none') {
    return sizes.map(([binCount, label]) =>
      toColumnSizeOption(binCount, label, viewSettings)
    );
  }

  const field = toPerValueFieldLabel(
    perValueAxes,
    viewSettings,
    numericFields,
    i18nLocale
  );
  return sizes.map(([binCount, size]) =>
    toColumnSizeOption(
      binCount,
      t(
        i18nLocale,
        'views:surface3d.columnSize.withPerValueAxis',
        { size, field },
        '{{size}}, one per value on {{field}}'
      ),
      viewSettings
    )
  );
};

const columnSizeDropdown = (args: ColumnSizeOptionsArgs): SettingsConfig => ({
  id: 'surface3dColumnSize',
  type: SettingsType.DROPDOWN,
  iconName: IconName.GRID_ON,
  label: t(args.i18nLocale, 'views:surface3d.menu.columnSize', 'Column size'),
  options: toColumnSizeOptions(args),
  dropdownSize: DropdownSize.S,
});

const appearanceDropdown = (
  viewSettings: Surface3DViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'surface3dAppearance',
  type: SettingsType.DROPDOWN,
  iconName: IconName.VISIBILITY,
  label: t(i18nLocale, 'views:surface3d.menu.appearance', 'Appearance'),
  options: [
    {
      name: 'surface3d_wireframe',
      label: t(
        i18nLocale,
        'views:surface3d.appearance.wireframe',
        'Mesh lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.showWireframe,
      onClick: () =>
        handleStoreSetting(VIEW_ID, {
          showWireframe: !viewSettings.showWireframe,
        }),
    },
    ...(viewSettings.interpolation === 'binned'
      ? []
      : [
          {
            name: 'surface3d_draped_grid',
            label: t(
              i18nLocale,
              'views:surface3d.appearance.drapedGrid',
              'Grid on surface'
            ),
            type: DropdownOptionType.OPTION,
            isActive: viewSettings.showDrapedGrid,
            onClick: () =>
              handleStoreSetting(VIEW_ID, {
                showDrapedGrid: !viewSettings.showDrapedGrid,
              }),
          },
          {
            name: 'surface3d_outlines',
            label: t(
              i18nLocale,
              'views:surface3d.appearance.outlines',
              'Outlines'
            ),
            type: DropdownOptionType.OPTION,
            isActive: viewSettings.showOutlines,
            onClick: () =>
              handleStoreSetting(VIEW_ID, {
                showOutlines: !viewSettings.showOutlines,
              }),
          },
        ]),
    {
      name: 'surface3d_droplines',
      label: t(
        i18nLocale,
        'views:surface3d.appearance.dropLines',
        'Drop lines to the base'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.showDropLines,
      onClick: () =>
        handleStoreSetting(VIEW_ID, {
          showDropLines: !viewSettings.showDropLines,
        }),
    },
    {
      name: 'surface3d_samples',
      label: t(
        i18nLocale,
        'views:surface3d.appearance.samplePositions',
        'Component positions'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.showSamplePositions,
      onClick: () =>
        handleStoreSetting(VIEW_ID, {
          showSamplePositions: !viewSettings.showSamplePositions,
        }),
    },
    {
      name: 'surface3d_contours',
      label: t(
        i18nLocale,
        'views:surface3d.appearance.contours',
        'Contour lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.contourCount > 0,
      /* A count rather than a boolean in the settings, so the number of lines stays tunable
         later without changing the persisted shape. */
      onClick: () =>
        handleStoreSetting(VIEW_ID, {
          contourCount: viewSettings.contourCount > 0 ? 0 : 8,
        }),
    },
  ],
  dropdownSize: DropdownSize.S,
  /* The one dropdown in this bar whose options are not mutually exclusive. `ModalBackdrop` closes
     on any content click unless told otherwise, so without this every toggle costs an open - the
     same reason the pages view's field and reference-type columns set it. */
  isKeepOpen: true,
});

export type Surface3DViewModifiersArgs = {
  viewSettings: Surface3DViewSettings;
  numericFields: NumericField[];
  /** Which axes get one bin per value. */
  perValueAxes: PerValueAxes;
  i18nLocale: I18nLocale;
};

/** The controls the settings bar shows. */
export const getSurface3DViewModifiers = ({
  viewSettings,
  numericFields,
  perValueAxes,
  i18nLocale,
}: Surface3DViewModifiersArgs): SettingsConfig[] => {
  return [
    ...FIELD_DIMENSIONS.map(dimension =>
      fieldPicker(dimension, viewSettings, numericFields, i18nLocale)
    ),
    interpolationDropdown(viewSettings, i18nLocale),
    aggregationDropdown(viewSettings, i18nLocale),
    viewSettings.interpolation === 'binned'
      ? columnSizeDropdown({
          viewSettings,
          perValueAxes,
          numericFields,
          i18nLocale,
        })
      : resolutionDropdown(viewSettings, i18nLocale),
    appearanceDropdown(viewSettings, i18nLocale),
  ];
};
